/**
 * The per-instance settings sections — one list, three consumers.
 *
 * There are three places a section has to be known: the settings sidenav and
 * mobile tabs (settings-shell), the Settings dropdown in the channel bar
 * (instance-settings-menu), and the route validator that decides whether
 * /a/<slug>/settings/<section> exists at all. They were three hand-kept copies,
 * and they drifted the first time a section was added: iCloud shipped in the
 * sidenav and the route but never appeared in the dropdown, so the only way to
 * reach it was to already know the URL.
 *
 * Adding a section is now one entry here. The route validator derives from the
 * same array, so a section can never be linkable-but-404 or reachable-but-
 * unlisted.
 */

import { msg } from "@lingui/core/macro";
import { isFortnoxConfigured } from "@shared/fortnox/oauth";
import { isBokioConfigured } from "@shared/bokio/oauth";
import { isOqtoConfigured } from "@shared/oqto/oauth";
import { isSpirisConfigured } from "@shared/spiris/oauth";
import { isVitecConfigured } from "@shared/vitec/auth";
import { hasFeature } from "@desk-schemas/features";
import { ALL_INTEGRATIONS, type IntegrationDef } from "./integrations";
import type { MessageDescriptor } from "@lingui/core";
import {
  SlidersHorizontalIcon,
  LinkIcon,
  UsersIcon,
  KeyRoundIcon,
  LogInIcon,
  MailIcon,
  CircleUserRoundIcon,
  Building2Icon,
  CloudIcon,
  PuzzleIcon,
  PlugZapIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * A deployment-level capability an INTEGRATION depends on.
 *
 * Some providers are configured per environment (dev has credentials, prod
 * doesn't yet). Their tile must not render at all on a deployment that cannot
 * complete the flow — a visible tile whose only button 503s reads as a broken
 * feature, not an unconfigured one, and it is the same artifact in both
 * environments, so this cannot be a build-time decision.
 *
 * Sections themselves are no longer gated: every remaining section is
 * unconditional, and the Integrations section stays visible even when one
 * provider inside it is unavailable.
 */
export type SettingsCapability = "fortnox" | "bokio" | "oqto" | "spiris" | "vitec" | "apis" | "logins";

export type SettingsCapabilities = Readonly<Record<SettingsCapability, boolean>>;

export interface SettingsSectionDef {
  readonly id: string;
  readonly label: MessageDescriptor;
  readonly icon: LucideIcon;
}

/**
 * Google, Microsoft, iCloud and MCP stay here as their own entries; the niche
 * vertical providers (Fortnox, and Bokio and Hemnet behind it) live under
 * "Integrations", registered in lib/integrations.ts.
 *
 * The split is by how many users recognise the name. The first three are
 * accounts almost everyone already has, and MCP is a general mechanism rather
 * than a vendor — burying those behind a generic label would make common things
 * harder to find in order to tidy away rare ones. The long tail is the opposite:
 * individually irrelevant to most users, and numerous enough that one section
 * each would push General and Members off the end of the list.
 *
 * Every entry here is unconditional. Availability of a niche provider is
 * decided one level down, per tile, so a deployment without Fortnox credentials
 * shows Integrations without that tile rather than hiding the section.
 */
export const SETTINGS_SECTIONS = [
  { id: "general", label: msg`General`, icon: SlidersHorizontalIcon },
  { id: "channels", label: msg`Channels`, icon: LinkIcon },
  { id: "members", label: msg`Members`, icon: UsersIcon },
  { id: "secrets", label: msg`Secrets`, icon: KeyRoundIcon },
  { id: "logins", label: msg`Logins`, icon: LogInIcon },
  { id: "email", label: msg`Email`, icon: MailIcon },
  { id: "google", label: msg`Google`, icon: CircleUserRoundIcon },
  { id: "microsoft", label: msg`Microsoft`, icon: Building2Icon },
  { id: "icloud", label: msg`iCloud`, icon: CloudIcon },
  { id: "integrations", label: msg`Integrations`, icon: PuzzleIcon },
  { id: "mcp", label: msg`MCP`, icon: PlugZapIcon },
] as const satisfies readonly SettingsSectionDef[];

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]["id"];

/** The section a path segment names, or null when it names no section. */
export const asSettingsSection = (value: string | undefined): SettingsSection | null =>
  SETTINGS_SECTIONS.find((s) => s.id === value)?.id ?? null;

/** Every capability off — the safe default when no provider is mounted. */
export const NO_SETTINGS_CAPABILITIES: SettingsCapabilities = {
  fortnox: false,
  bokio: false,
  oqto: false,
  spiris: false,
  vitec: false,
  apis: false,
  logins: false,
};

/**
 * Which optional settings sections one instance may show.
 *
 * Two gates. The DEPLOYMENT's: whether this box can reach the provider at all
 * — read at request time, never baked in, because the same artifact is
 * promoted dev→prod. That is how an integration ships dark. And, for a
 * provider still in a pilot, the pilot's feature flag: whether this customer
 * is in it. Fortnox and Bokio dropped their flags at GA on 2026-09-08 and
 * Oqto on 2026-09-25, so they are deployment-gated only. Vitec carries the
 * `vitec` ORGANIZATION flag until its pilot concludes (every agent a realtor
 * firm owns, not one at a time), and Spiris the `spiris` one. The `apis`
 * tile was the last on an INSTANCE flag (`api_connect`, GA 2026-10-23), so
 * only the organization's flags are threaded in.
 *
 * Presentation only. It decides which tab renders and nothing more — hiding a
 * tab stops nobody from POSTing, so the routes enforce the real conditions
 * themselves via `verifyFortnoxAccess` / `verifyVitecAccess`.
 */
export const settingsCapabilities = (
  orgFeatureFlags: readonly string[] = [],
  instanceFeatureFlags: readonly string[] = [],
): SettingsCapabilities => ({
  fortnox: isFortnoxConfigured(),
  // Independently gated: Bokio registers an app as "test" or "prod" mode, so a
  // deployment can hold credentials for one provider and not the other.
  bokio: isBokioConfigured(),
  // One Oqto application per environment, like Bokio.
  oqto: isOqtoConfigured(),
  // Not OAuth: one partner Basic credential per deployment, AND the agent's
  // organization must be in the pilot. Either absent = the realtor tile does
  // not render.
  // One OAuth client per environment, like Bokio.
  spiris: isSpirisConfigured(),
  vitec: isVitecConfigured() && hasFeature(orgFeatureFlags, "vitec"),
  // OAuth APIs the agent signs in to itself (`api connect`): nothing to
  // configure per deployment, so every agent has it.
  apis: true,
  // Sign-ins handed to the agent (`request_user_auth`): only agents in the
  // pilot can be handed one, so only they have logins to list.
  logins: hasFeature(instanceFeatureFlags, "auth_handoff"),
});

/**
 * Which providers this instance may see, in canonical order.
 */
export const availableIntegrations = (
  capabilities: SettingsCapabilities,
): readonly IntegrationDef[] =>
  ALL_INTEGRATIONS.filter((i) => i.capability === undefined || capabilities[i.capability]);

/**
 * The sections to render for this instance.
 *
 * Only one is ever dropped, and it is dropped for a derived reason rather than
 * a declared capability: "Integrations" holds the niche providers, every one of
 * them is individually gated, and an instance in no pilot therefore has an
 * empty section. Showing a menu entry that opens onto nothing is worse than not
 * showing it — it advertises a feature the user cannot reach and cannot be told
 * how to reach.
 *
 * This is deliberately NOT a return of the old per-section `capability` field.
 * That was a declared flag on a section, and it was removed because nothing
 * declared it any more. This is computed from what the section actually
 * contains, so it cannot drift out of agreement with the tiles.
 */
export const visibleSettingsSections = (
  capabilities: SettingsCapabilities,
): readonly SettingsSectionDef[] => {
  const hasIntegrations = availableIntegrations(capabilities).length > 0;
  // Logins, likewise, holds nothing for an agent no one can hand a sign-in to.
  return SETTINGS_SECTIONS.filter((s) =>
    (s.id !== "integrations" || hasIntegrations) && (s.id !== "logins" || capabilities.logins));
};
